import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Search, Download, Table2, LayoutList } from 'lucide-react';
import type { CockpitRepRow, CockpitUnattributed, CockpitSeason } from '../../types';
import { pickWindow, seasonLabel, type FundMetric } from '../../lib/fundraisingWindow';
import { formatDollarsCompact } from '../../lib/snapshotMetrics';
import { sortRepRows, type SortKey, type SortDir } from './repTable/sortRows';
import { repRowsToCsv } from './repTable/repCsv';
import { ACTIVITY_TYPE_COLUMNS, byTypeKey, sumTypeCount, typeCount } from './repTable/activityTypes';
import { ACTIVITY_ICONS } from '../activities/activityVisuals';
import { YoyPill } from './YoyPill';
import { RepFundraiserPanel } from './RepFundraiserPanel';
import { OnsiteFlagsPanel } from './OnsiteFlagsPanel';

function lastSeenLabel(iso: string | null): string {
  if (!iso) return 'Never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

function lastSeenClass(iso: string | null): string {
  if (!iso) return 'text-rose-600 dark:text-rose-400';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days >= 7) return 'text-amber-600 dark:text-amber-400';
  return 'text-gray-500 dark:text-gray-400';
}

function rankClass(rank: number): string {
  if (rank === 1) return 'bg-amber-400 text-white';
  if (rank === 2) return 'bg-gray-300 text-gray-800';
  if (rank === 3) return 'bg-amber-700 text-white';
  return 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

function sumCents(rows: CockpitRepRow[], key: 'raisedThisSyCents' | 'raisedPriorSyCents'): bigint {
  return rows.reduce((acc, r) => acc + BigInt(r[key]), 0n);
}
function sumInt(rows: CockpitRepRow[], key: keyof CockpitRepRow): number {
  return rows.reduce((acc, r) => acc + (r[key] as number), 0);
}

/**
 * The table is split into two views that share the rank + rep column. Fundraising
 * carries the money and coverage numbers; Activity carries what each rep actually
 * did, with calls / texts / emails / visits broken out so a manager can rank the
 * team by any one of them. Each view fits a laptop without sideways scrolling,
 * which one combined table never did once the type columns arrived.
 *
 * The card view (phones, or the Cards toggle) has no tab strip or sort headers,
 * so it always lists reps in rank order and never inherits a table-only sort.
 */
type Tab = 'fundraising' | 'activity';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'fundraising', label: 'Fundraising' },
  { key: 'activity', label: 'Activity' },
];
const TAB_STORAGE_KEY = 'teamRepTableTab';
const tabId = (t: Tab) => `rep-table-tab-${t}`;
const PANEL_ID = 'rep-table-panel';

function initialTab(): Tab {
  try { return localStorage.getItem(TAB_STORAGE_KEY) === 'activity' ? 'activity' : 'fundraising'; } catch { return 'fundraising'; }
}

type Sort = { key: SortKey; dir: SortDir };
const RANK_SORT: Sort = { key: 'raisedThisSyCents', dir: 'desc' };
const DEFAULT_SORTS: Record<Tab, Sort> = { fundraising: RANK_SORT, activity: { key: 'activitiesTotal', dir: 'desc' } };

type UnattribWindow = NonNullable<ReturnType<typeof pickWindow>>;

interface Column {
  key: SortKey;
  label: string;
  icon?: typeof Search;
  title?: string;
  cell: (r: CockpitRepRow) => ReactNode;
  foot: (rows: CockpitRepRow[]) => ReactNode;
  /** What the Unattributed row shows under this column; columns without one stay blank. */
  unattrib?: (u: UnattribWindow) => ReactNode;
  /** Overrides the default numeric cell styling. */
  tdClass?: string | ((r: CockpitRepRow) => string);
}

const NUM_TD = 'px-3 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-200';
const UNATTRIB_TD = 'px-3 py-2.5 text-right tabular-nums text-amber-900 dark:text-amber-200';

export function RepAccountabilityTable(
  { rows, period, teamId, metric = 'calYtd', unattributed = null, season = null }: {
    rows: CockpitRepRow[]; period?: { from: string; to: string }; teamId?: string | null;
    metric?: FundMetric; unattributed?: CockpitUnattributed | null;
    season?: CockpitSeason | null;
  },
) {
  // CRM-121: one vocabulary for one window. The season columns name the actual
  // season ("$ 2026-27"), matching the pill, the tile and the CSV — the page used
  // to have four different names for the same thing.
  const moneyHeaders = metric === 'calYtd'
    ? { current: '$ YTD', prior: '$ Last YTD' }
    : season
      ? { current: `$ ${seasonLabel(season.currentSeasonYear)}`, prior: `$ ${seasonLabel(season.priorSeasonYear)}` }
      : { current: '$ Season', prior: '$ Last Season' };

  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Tab>(initialTab);
  // Each tab keeps its own sort, so flipping across to check something and back doesn't lose your place.
  const [sorts, setSorts] = useState<Record<Tab, Sort>>(() => ({ ...DEFAULT_SORTS }));
  const sort = sorts[tab];
  const [view, setView] = useState<'table' | 'cards'>(() =>
    (typeof localStorage !== 'undefined' && localStorage.getItem('teamRepTableView') === 'cards') ? 'cards' : 'table');
  const [selected, setSelected] = useState<{ rep: CockpitRepRow; rank: number } | null>(null);
  const [flagsFor, setFlagsFor] = useState<CockpitRepRow | 'all' | null>(null);
  const totalFlagged = useMemo(() => rows.reduce((a, r) => a + r.onsiteFlagged, 0), [rows]);

  // Unattributed follows the YTD/Season toggle like the rep rows do.
  const unattrib = unattributed ? pickWindow(unattributed.windows, metric, { season: unattributed.season }) : null;

  const FUNDRAISING: Column[] = [
    { key: 'raisedThisSyCents', label: moneyHeaders.current, tdClass: 'px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100',
      cell: (r) => formatDollarsCompact(r.raisedThisSyCents),
      unattrib: (u) => formatDollarsCompact(u.raisedCents),
      foot: (v) => formatDollarsCompact((sumCents(v, 'raisedThisSyCents') + BigInt(unattrib?.raisedCents ?? '0')).toString()) },
    { key: 'raisedPriorSyCents', label: moneyHeaders.prior, tdClass: 'px-3 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400',
      cell: (r) => formatDollarsCompact(r.raisedPriorSyCents),
      unattrib: (u) => formatDollarsCompact(u.priorCents),
      foot: (v) => formatDollarsCompact((sumCents(v, 'raisedPriorSyCents') + BigInt(unattrib?.priorCents ?? '0')).toString()) },
    { key: 'fundraiserYoyPct', label: 'YoY', tdClass: 'px-3 py-2.5 text-right', cell: (r) => <YoyPill pct={r.fundraiserYoyPct} />, foot: () => null },
    { key: 'campaignsThisSy', label: 'Fundraisers', cell: (r) => r.campaignsThisSy, unattrib: (u) => u.campaigns, foot: (v) => sumInt(v, 'campaignsThisSy') + (unattrib?.campaigns ?? 0) },
    { key: 'conversions', label: 'Conv', tdClass: 'px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100', cell: (r) => r.conversions, foot: (v) => sumInt(v, 'conversions') },
    { key: 'peopleOwned', label: 'People', cell: (r) => r.peopleOwned, foot: (v) => sumInt(v, 'peopleOwned') },
    { key: 'namedAccountsOwned', label: 'Accts', cell: (r) => r.namedAccountsOwned, foot: (v) => sumInt(v, 'namedAccountsOwned') },
    { key: 'lastSeen', label: 'Last seen', tdClass: (r) => `px-3 py-2.5 text-right whitespace-nowrap ${lastSeenClass(r.lastSeen)}`, cell: (r) => lastSeenLabel(r.lastSeen), foot: () => null },
  ];

  const ACTIVITY: Column[] = [
    { key: 'activitiesTotal', label: 'Activities', title: 'All activity in the period, including notes and other', cell: (r) => r.activitiesTotal, foot: (v) => sumInt(v, 'activitiesTotal') },
    ...ACTIVITY_TYPE_COLUMNS.map<Column>((t) => ({
      key: byTypeKey(t.type), label: t.label, icon: ACTIVITY_ICONS[t.type], title: t.title,
      cell: (r) => typeCount(r, t.type), foot: (v) => sumTypeCount(v, t.type),
    })),
    { key: 'onsite', label: 'Onsite', foot: (v) => sumInt(v, 'onsite'),
      cell: (r) => (
        <>
          <div>{r.onsite}</div>
          {r.onsite > 0 && (
            <div className="text-[10px] text-gray-400 dark:text-gray-500">
              <span className="text-emerald-600 dark:text-emerald-400">✓{r.onsiteVerified}</span>{' '}
              <span>○{r.onsiteUnverified}</span>
            </div>
          )}
        </>
      ) },
    { key: 'onsiteFlagged', label: 'Review', tdClass: 'px-3 py-2.5 text-right tabular-nums', foot: (v) => sumInt(v, 'onsiteFlagged'),
      cell: (r) => r.onsiteFlagged > 0 ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setFlagsFor(r); }}
          className="font-semibold text-rose-600 dark:text-rose-400 hover:underline"
        >
          {r.onsiteFlagged}
        </button>
      ) : (
        <span className="text-gray-300 dark:text-gray-600">0</span>
      ) },
    { key: 'contactsTouched', label: 'Touched', cell: (r) => r.contactsTouched, foot: (v) => sumInt(v, 'contactsTouched') },
    { key: 'overdue', label: 'Overdue', tdClass: (r) => `px-3 py-2.5 text-right tabular-nums ${r.overdue > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-700 dark:text-gray-200'}`, cell: (r) => r.overdue, foot: (v) => sumInt(v, 'overdue') },
    { key: 'lastSeen', label: 'Last seen', tdClass: (r) => `px-3 py-2.5 text-right whitespace-nowrap ${lastSeenClass(r.lastSeen)}`, cell: (r) => lastSeenLabel(r.lastSeen), foot: () => null },
  ];

  const columns = tab === 'fundraising' ? FUNDRAISING : ACTIVITY;

  function chooseView(v: 'table' | 'cards') {
    setView(v);
    try { localStorage.setItem('teamRepTableView', v); } catch { /* ignore */ }
  }

  function chooseTab(t: Tab) {
    setTab(t);
    try { localStorage.setItem(TAB_STORAGE_KEY, t); } catch { /* ignore */ }
  }

  // Arrow keys move between tabs and follow focus, per the ARIA tabs pattern.
  function onTabKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const i = TABS.findIndex((t) => t.key === tab);
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    const target = TABS[next].key;
    chooseTab(target);
    document.getElementById(tabId(target))?.focus();
  }

  function toggleSort(key: SortKey) {
    setSorts((s) => {
      const cur = s[tab];
      return { ...s, [tab]: cur.key === key ? { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' } };
    });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => (r.displayName ?? r.userId).toLowerCase().includes(q)) : rows;
  }, [rows, query]);
  const visible = useMemo(() => sortRepRows(filtered, sort.key, sort.dir), [filtered, sort]);
  const cardRows = useMemo(() => sortRepRows(filtered, RANK_SORT.key, RANK_SORT.dir), [filtered]);

  // Rank is by money in the SELECTED window, across ALL reps — it ignores search
  // and column sort, but it does follow the YTD/season toggle, because `rows`
  // arrives already mapped to that window. CRM-121: the old comment here claimed
  // the rank was pinned outright, which was never true and made the re-order look
  // like a bug. It isn't — a YTD view should rank by YTD — so the badge now says
  // what it ranks by instead of pretending to be absolute.
  const rankByUserId = useMemo(() => {
    const m = new Map<string, number>();
    sortRepRows(rows, RANK_SORT.key, RANK_SORT.dir).forEach((r, i) => m.set(r.userId, i + 1));
    return m;
  }, [rows]);
  const rankBasisLabel = `Rank by ${moneyHeaders.current.replace(/^\$ /, '')} across all reps`;

  function exportCsv() {
    const blob = new Blob([repRowsToCsv(visible, moneyHeaders)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'team-reps.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (rows.length === 0 && !unattributed) {
    return <div className="card p-6 text-sm text-gray-400 dark:text-gray-500 text-center">No reps yet.</div>;
  }

  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'desc' ? '▾' : '▴') : '');
  const tdClassFor = (c: Column, r: CockpitRepRow) =>
    typeof c.tdClass === 'function' ? c.tdClass(r) : (c.tdClass ?? NUM_TD);

  return (
    <>
    {totalFlagged > 0 && (
      <button
        type="button"
        onClick={() => setFlagsFor('all')}
        className="mb-3 w-full flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/30"
      >
        🚩 <span className="font-semibold">{totalFlagged}</span> on-campus visit{totalFlagged === 1 ? '' : 's'} need review
      </button>
    )}
    <div className="card overflow-hidden">
      {/* Toolbar */}
      <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reps…"
            className="bg-transparent outline-none text-sm w-full text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-full px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          {/* Desktop-only View toggle */}
          <div className="hidden sm:inline-flex bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5">
            <button type="button" onClick={() => chooseView('table')} aria-pressed={view === 'table'} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md ${view === 'table' ? 'bg-white dark:bg-gray-700 text-blast-700 dark:text-blast-300 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
              <Table2 className="w-3.5 h-3.5" /> Table
            </button>
            <button type="button" onClick={() => chooseView('cards')} aria-pressed={view === 'cards'} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md ${view === 'cards' ? 'bg-white dark:bg-gray-700 text-blast-700 dark:text-blast-300 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
              <LayoutList className="w-3.5 h-3.5" /> Cards
            </button>
          </div>
        </div>
      </div>

      {/* Tab strip — only meaningful where the table is, so it follows the table's visibility */}
      <div role="tablist" aria-label="Table view" className={`${view === 'table' ? 'hidden sm:flex' : 'hidden'} gap-1 px-3 border-b border-gray-100 dark:border-gray-700`}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={tabId(t.key)}
            aria-selected={tab === t.key}
            aria-controls={PANEL_ID}
            tabIndex={tab === t.key ? 0 : -1}
            onClick={() => chooseTab(t.key)}
            onKeyDown={onTabKeyDown}
            className={`-mb-px px-3 pt-2.5 pb-2 text-sm font-semibold border-b-2 ${tab === t.key ? 'text-blast-700 dark:text-blast-300 border-blast-500' : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TABLE VIEW — desktop when view=table; never on mobile */}
      <div role="tabpanel" id={PANEL_ID} aria-labelledby={tabId(tab)} className={`${view === 'table' ? 'hidden sm:block' : 'hidden'} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              <th scope="col" className="px-4 py-2 font-semibold cursor-pointer" onClick={() => toggleSort('displayName')}>Rep {arrow('displayName')}</th>
              {columns.map((c) => (
                <th key={c.key} scope="col" title={c.title} className={`px-3 py-2 font-semibold text-right cursor-pointer whitespace-nowrap ${sort.key === c.key ? 'text-blast-600 dark:text-blast-300' : ''}`} onClick={() => toggleSort(c.key)}>
                  {c.icon && <c.icon className="inline w-3 h-3 mr-1 -mt-0.5" aria-hidden="true" />}
                  {c.label} {arrow(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const rank = rankByUserId.get(r.userId) ?? 0;
              return (
              <tr key={r.userId} onClick={() => setSelected({ rep: r, rank })} className="border-t border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                  <span className="inline-flex items-center gap-2">
                    <span title={rankBasisLabel} className={`w-5 h-5 rounded text-[10px] font-bold inline-flex items-center justify-center ${rankClass(rank)}`}>{rank}</span>
                    {r.displayName || r.userId}
                  </span>
                </td>
                {columns.map((c) => <td key={c.key} className={tdClassFor(c, r)}>{c.cell(r)}</td>)}
              </tr>
              );
            })}
            {visible.length === 0 && tab === 'activity' && (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                  No rep activity in this period.
                </td>
              </tr>
            )}
            {unattrib && tab === 'fundraising' && (
              <tr className="bg-amber-50/60 dark:bg-amber-900/10 border-t border-amber-200 dark:border-amber-800/50">
                <td className="px-4 py-2.5">
                  <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Unattributed</span>
                  <span className="block text-[11px] text-amber-700/70 dark:text-amber-400/70">
                    former, deactivated, or unassigned reps
                  </span>
                </td>
                {columns.map((c) => (
                  <td key={c.key} className={c.unattrib ? UNATTRIB_TD : 'px-3 py-2.5'}>{c.unattrib ? c.unattrib(unattrib) : null}</td>
                ))}
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 dark:bg-gray-800/70 border-t-2 border-gray-200 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
              <td className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Team total · {visible.length} reps</td>
              {columns.map((c) => <td key={c.key} className="px-3 py-2.5 text-right tabular-nums">{c.foot(visible)}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* CARD VIEW — mobile always; desktop when view=cards. Always in rank order. */}
      <div className={view === 'table' ? 'sm:hidden divide-y divide-gray-50 dark:divide-gray-700/50' : 'divide-y divide-gray-50 dark:divide-gray-700/50'}>
        {cardRows.map((r) => {
          const rank = rankByUserId.get(r.userId) ?? 0;
          return (
          <button type="button" key={r.userId} onClick={() => setSelected({ rep: r, rank })} className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/40">
            <div className="flex items-center gap-3">
              <span title={rankBasisLabel} className={`w-5 h-5 rounded text-[10px] font-bold inline-flex items-center justify-center ${rankClass(rank)}`}>{rank}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100 tabular-nums">{formatDollarsCompact(r.raisedThisSyCents)}</span>
                  <YoyPill pct={r.fundraiserYoyPct} />
                </div>
                <div className="mt-0.5 text-xs text-gray-700 dark:text-gray-200">
                  <span className="font-medium">{r.displayName || r.userId}</span> · {r.campaignsThisSy} fundraisers · {formatDollarsCompact(r.raisedPriorSyCents)} last yr
                </div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {r.activitiesTotal} activities · {r.onsite} onsite (<span className="text-emerald-600 dark:text-emerald-400">✓{r.onsiteVerified}</span> ○{r.onsiteUnverified}
                  {r.onsiteFlagged > 0 && (
                    <> · <span role="button" onClick={(e) => { e.stopPropagation(); setFlagsFor(r); }} className="text-rose-600 dark:text-rose-400 font-semibold">🚩{r.onsiteFlagged}</span></>
                  )}) · {r.conversions} conv · {r.overdue} overdue · {lastSeenLabel(r.lastSeen)}
                </div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {ACTIVITY_TYPE_COLUMNS.map((t) => `${typeCount(r, t.type)} ${t.label.toLowerCase()}`).join(' · ')}
                </div>
              </div>
            </div>
          </button>
          );
        })}
        {unattrib && (
          <div className="px-4 py-3 bg-amber-50/60 dark:bg-amber-900/10 border-t border-amber-200 dark:border-amber-800/50">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Unattributed</span>
              <span className="text-base font-bold tabular-nums text-amber-900 dark:text-amber-200">
                {formatDollarsCompact(unattrib.raisedCents)}
              </span>
            </div>
            <div className="mt-0.5 text-[11px] text-amber-700/70 dark:text-amber-400/70">
              {unattrib.campaigns} fundraisers · former, deactivated, or unassigned reps
            </div>
          </div>
        )}
      </div>
    </div>
    <RepFundraiserPanel rep={selected?.rep ?? null} rank={selected?.rank} onClose={() => setSelected(null)} />
    <OnsiteFlagsPanel
      open={flagsFor !== null}
      onClose={() => setFlagsFor(null)}
      period={period}
      teamId={teamId ?? null}
      repId={flagsFor && flagsFor !== 'all' ? flagsFor.userId : undefined}
      repName={flagsFor && flagsFor !== 'all' ? (flagsFor.displayName ?? flagsFor.userId) : undefined}
    />
    </>
  );
}
